import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  TaskCheckbox,
  TaskDialog,
  TaskDialogActions,
  TaskMessage,
  TaskTextInput,
} from "./taskFormUi";

const AirportPickupForm = ({ projectId, onClose, onSave }) => {
  const [flightNumber, setFlightNumber] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [numAdults, setNumAdults] = useState("1");
  const [numChildren, setNumChildren] = useState("0");
  const [numBags, setNumBags] = useState("1");
  const [additionalLuggage, setAdditionalLuggage] = useState(false);
  const [existingRecord, setExistingRecord] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchAirportDetails = async () => {
      if (!projectId) return;

      const { data } = await taskDetailApi.list("flight-arrivals", {
        project_id: projectId,
        limit: 1,
      });

      if (data?.[0]) {
        const row = data[0];
        setExistingRecord(row);
        setFlightNumber(row.flight_number || "");
        setPickupAddress(row.pickup_address || "");
        setNumAdults(String(row.number_of_adults || 1));
        setNumChildren(String(row.number_of_children || 0));
        setNumBags(String(row.number_of_bags || 1));
        setAdditionalLuggage(Boolean(row.additional_luggage));
      }
    };

    fetchAirportDetails();
  }, [projectId]);

  const handleSave = async (event) => {
    event?.preventDefault();
    if (!projectId) return;
    setErrorMessage("");
    setSuccessMessage("");

    if (!flightNumber.trim()) {
      setErrorMessage("Flight number is required");
      return;
    }
    if (!pickupAddress.trim()) {
      setErrorMessage("Pickup address is required");
      return;
    }

    const pickupData = {
      flight_number: flightNumber,
      pickup_address: pickupAddress,
      number_of_adults: Number(numAdults),
      number_of_children: Number(numChildren),
      number_of_bags: Number(numBags),
      additional_luggage: additionalLuggage,
      project_id: projectId,
    };

    const { error } = existingRecord?.id
      ? await taskDetailApi.update(
          "flight-arrivals",
          existingRecord.id,
          pickupData,
        )
      : await taskDetailApi.create("flight-arrivals", pickupData);

    if (error) {
      setErrorMessage("Failed to save airport pickup details");
      return;
    }

    setSuccessMessage("Airport pickup details saved successfully.");
    onSave?.();
    onClose?.();
  };

  return (
    <TaskDialog
      title="Airport Pickup Details"
      description="Flight arrival and transfer requirements for this project."
      onClose={onClose}
      className="max-w-xl"
    >
      <form onSubmit={handleSave} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>
        <TaskMessage tone="success">{successMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="flight-number"
            label="Flight Number"
            value={flightNumber}
            onChange={(event) => setFlightNumber(event.target.value)}
            required
          />
          <TaskTextInput
            id="pickup-address"
            label="Pickup Address"
            value={pickupAddress}
            onChange={(event) => setPickupAddress(event.target.value)}
            required
          />
          <TaskTextInput
            id="adult-count"
            label="Number of Adults"
            type="number"
            min={0}
            value={numAdults}
            onChange={(event) => setNumAdults(event.target.value)}
          />
          <TaskTextInput
            id="children-count"
            label="Number of Children"
            type="number"
            min={0}
            value={numChildren}
            onChange={(event) => setNumChildren(event.target.value)}
          />
          <TaskTextInput
            id="bag-count"
            label="Number of Bags"
            type="number"
            min={0}
            value={numBags}
            onChange={(event) => setNumBags(event.target.value)}
          />
          <TaskCheckbox
            label="Additional luggage"
            checked={additionalLuggage}
            onCheckedChange={(checked) =>
              setAdditionalLuggage(checked === true)
            }
            className="self-end"
          />
        </div>

        <TaskDialogActions onCancel={onClose} submitType="submit" />
      </form>
    </TaskDialog>
  );
};

export default AirportPickupForm;
